import type { ReactNode } from "react";
import { Link } from "react-router";
import { ArrowRight, CircleCheck, Clock, FileKey, Fingerprint, KeyRound, LifeBuoy, Repeat, ShieldAlert, ShieldCheck, ShieldX, TriangleAlert } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { paths } from "../../app/paths.js";
import { isDesktop } from "../../app/platform.js";
import type { BreachStatus } from "../../data/breach.js";
import type { HealthReport } from "../../lib/health.js";
import { Card, Ring, Skeleton } from "../../ui/primitives.js";

export interface AccountSecurity {
  readonly totpEnabled: boolean | undefined;
  readonly passkeys: number | undefined;
  readonly backupCodes: number | undefined;
  /** Whether an emergency kit exists; undefined while loading. */
  readonly recoveryKit: boolean | undefined;
}

interface CheckItem {
  readonly id: string;
  readonly done: boolean;
  readonly label: string;
  readonly hint: string;
  readonly to: string;
  readonly icon: LucideIcon;
}

const MIN_BACKUP_CODES = 3;

function verdict(score: number, total: number): { readonly title: string; readonly tone: string } {
  if (total === 0) {
    return { title: "Noch keine Daten", tone: "text-mist" };
  }
  if (score >= 90) {
    return { title: "Ausgezeichnet", tone: "text-flow" };
  }
  if (score >= 70) {
    return { title: "Gut, mit Luft nach oben", tone: "text-flow" };
  }
  if (score >= 50) {
    return { title: "Verbesserungswürdig", tone: "text-mist" };
  }
  return { title: "Kritisch", tone: "text-buoy" };
}

function MetricLink(props: {
  readonly to: string;
  readonly icon: LucideIcon;
  readonly label: string;
  readonly count: number;
  readonly total: number;
  /** Shown instead of the count while the number is not known (check off, running or failed). */
  readonly placeholder?: string | undefined;
  readonly title?: string | undefined;
}): ReactNode {
  const Icon = props.icon;
  if (props.placeholder !== undefined) {
    return (
      <Link
        to={paths.settings}
        title={props.title}
        className="group flex flex-col gap-2 rounded-lg border border-line bg-ink px-3.5 py-3 transition-colors hover:border-line-strong hover:bg-ink-3/60"
      >
        <span className="flex items-center gap-2 text-[0.8125rem] text-mist">
          <Icon size={14} aria-hidden="true" className="shrink-0 text-mist-2" />
          <span className="truncate">{props.label}</span>
        </span>
        <span className="font-display text-2xl font-bold leading-none text-mist-2">–</span>
        <span className="truncate text-[0.7rem] text-mist-2">{props.placeholder}</span>
      </Link>
    );
  }
  const ok = props.count === 0;
  const share = props.total === 0 ? 0 : Math.round((props.count / props.total) * 100);
  return (
    <Link
      to={props.to}
      className="group flex flex-col gap-2 rounded-lg border border-line bg-ink px-3.5 py-3 transition-colors hover:border-line-strong hover:bg-ink-3/60"
    >
      <span className="flex items-center justify-between gap-2 text-[0.8125rem] text-mist">
        <span className="flex min-w-0 items-center gap-2">
          <Icon size={14} aria-hidden="true" className={ok ? "shrink-0 text-flow" : "shrink-0 text-buoy"} />
          <span className="truncate">{props.label}</span>
        </span>
        <ArrowRight size={13} aria-hidden="true" className="shrink-0 text-mist-2 transition-transform group-hover:translate-x-0.5 group-hover:text-flow" />
      </span>
      <span className={`font-display text-2xl font-bold tabular-nums leading-none ${ok ? "text-foam" : "text-buoy"}`}>{props.count}</span>
      <span className="h-1 overflow-hidden rounded-full bg-ink-4" aria-hidden="true">
        <span className={`block h-full rounded-full ${ok ? "bg-flow" : "bg-buoy"}`} style={{ width: `${ok ? 100 : Math.max(share, 6)}%` }} />
      </span>
    </Link>
  );
}

/** Hero card: password health score, the three issue classes, and an account-hardening checklist. */
const BREACH_PLACEHOLDER: Record<BreachStatus, string | undefined> = {
  off: "Prüfung aus",
  checking: "Wird geprüft …",
  error: "Nicht erreichbar",
  done: undefined
};

export function SecurityOverview(props: {
  readonly health: HealthReport;
  readonly breach: BreachStatus;
  readonly account: AccountSecurity;
  readonly loading: boolean;
}): ReactNode {
  const { health, account } = props;
  const v = verdict(health.score, health.total);

  const allChecks: ReadonlyArray<CheckItem> = [
    { id: "totp", done: account.totpEnabled === true, label: "Zwei-Faktor-Schutz", hint: "Authenticator-App einrichten", to: `${paths.settings}?setup=totp`, icon: ShieldCheck },
    { id: "recovery", done: account.recoveryKit !== false, label: "Notfall-Kit erstellt", hint: "Notfall-Kit erstellen", to: `${paths.settings}?setup=recovery`, icon: FileKey },
    { id: "passkey", done: (account.passkeys ?? 0) > 0, label: "Passkey hinterlegt", hint: "Gerät als Schlüssel nutzen", to: paths.settings, icon: Fingerprint },
    {
      id: "backup",
      done: account.totpEnabled !== true || (account.backupCodes ?? 0) >= MIN_BACKUP_CODES,
      label: "Backup-Codes vorrätig",
      hint: "Neue Codes erzeugen",
      to: paths.settings,
      icon: LifeBuoy
    },
    { id: "breached", done: health.breachedIds.length === 0, label: "Keine geleakten Passwörter", hint: "Geleakte ersetzen", to: "/vault?filter=breached", icon: ShieldX },
    { id: "weak", done: health.weakIds.length === 0, label: "Keine schwachen Passwörter", hint: "Schwache ersetzen", to: "/vault?filter=weak", icon: KeyRound },
    { id: "reused", done: health.reusedIds.length === 0, label: "Keine Wiederverwendung", hint: "Doppelte ersetzen", to: "/vault?filter=reused", icon: Repeat },
    { id: "old", done: health.oldIds.length === 0, label: "Alles aktuell", hint: "Alte Passwörter erneuern", to: "/vault?filter=old", icon: Clock }
  ];
  // Passkeys cannot be registered from the desktop app (WebAuthn origin), so it is not a task there.
  // Only a finished breach check can tick or flag its item.
  const checks = allChecks.filter((c: CheckItem): boolean => !(c.id === "passkey" && isDesktop()) && !(c.id === "breached" && props.breach !== "done"));
  const accountKnown = account.totpEnabled !== undefined;
  const doneCount = checks.filter((c: CheckItem): boolean => c.done).length;

  return (
    <Card className="relative overflow-hidden">
      <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 size-72 rounded-full bg-flow/[0.07] blur-3xl" />
      <div className="relative grid gap-6 p-5 sm:p-6 md:grid-cols-[auto_minmax(0,1fr)] md:items-center">
        {props.loading ? (
          <>
            <Skeleton className="size-[132px] rounded-full" />
            <div className="space-y-3">
              <Skeleton className="h-6 w-48" />
              <div className="grid grid-cols-3 gap-3">
                <Skeleton className="h-20" />
                <Skeleton className="h-20" />
                <Skeleton className="h-20" />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-5 md:flex-col md:gap-2">
              <div className="relative grid shrink-0 place-items-center">
                <Ring value={health.score} label={`Sicherheitswert ${health.score} von 100`} size={132} />
                <span className="absolute flex flex-col items-center">
                  <span className="font-display text-[2rem] font-bold tabular-nums leading-none text-foam">{health.score}</span>
                  <span className="mt-1 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-mist-2">von 100</span>
                </span>
              </div>
              <p className="text-xs text-mist-2 md:text-center">
                {health.healthyCount} von {health.total} sauber
              </p>
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-mist-2">Passwort-Gesundheit</p>
              <h2 className={`font-display mt-1 text-2xl font-bold tracking-[-0.01em] ${v.tone}`}>{v.title}</h2>
              <p className="mt-1 text-sm text-mist" title="An Have I Been Pwned gehen nur die ersten 5 Zeichen eines SHA-1-Hashwerts.">
                Lokal geprüft – Passwörter verlassen dein Gerät nie, auch nicht beim Datenleck-Abgleich.
              </p>
              <div className="mt-4 grid grid-cols-2 gap-2.5">
                <MetricLink
                  to="/vault?filter=breached"
                  icon={ShieldX}
                  label="Geleakt"
                  count={health.breachedIds.length}
                  total={health.total}
                  placeholder={BREACH_PLACEHOLDER[props.breach]}
                  title={props.breach === "off" ? "In den Einstellungen einschalten" : undefined}
                />
                <MetricLink to="/vault?filter=weak" icon={ShieldAlert} label="Schwach" count={health.weakIds.length} total={health.total} />
                <MetricLink to="/vault?filter=reused" icon={Repeat} label="Mehrfach" count={health.reusedIds.length} total={health.total} />
                <MetricLink to="/vault?filter=old" icon={Clock} label="Älter als 1 Jahr" count={health.oldIds.length} total={health.total} />
              </div>
            </div>
          </>
        )}
      </div>

      <div className="relative border-t border-line px-5 py-4 sm:px-6">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-foam">Sicherheits-Checkliste</h3>
          <span className="text-xs tabular-nums text-mist-2">
            {accountKnown ? `${doneCount} von ${checks.length} erledigt` : "Wird geprüft …"}
          </span>
        </div>
        <div className="mt-2 h-1 overflow-hidden rounded-full bg-ink-4" aria-hidden="true">
          <span className="block h-full rounded-full bg-flow transition-[width] duration-700 ease-flow" style={{ width: `${(doneCount / checks.length) * 100}%` }} />
        </div>
        <ul className="mt-3 grid gap-x-4 gap-y-0.5 sm:grid-cols-2">
          {checks.map((check: CheckItem) => {
            const Icon = check.done ? CircleCheck : TriangleAlert;
            return (
              <li key={check.id}>
                {check.done ? (
                  <span className="flex items-center gap-2.5 rounded-md px-1.5 py-1.5 text-sm text-mist">
                    <Icon size={15} aria-hidden="true" className="shrink-0 text-flow" />
                    <span className="truncate">{check.label}</span>
                    <span className="sr-only">(erledigt)</span>
                  </span>
                ) : (
                  <Link to={check.to} title={check.hint} className="group flex items-center gap-2.5 rounded-md px-1.5 py-1.5 text-sm text-foam transition-colors hover:bg-buoy/[0.06]">
                    <Icon size={15} aria-hidden="true" className="shrink-0 text-buoy" />
                    <span className="min-w-0 flex-1 truncate">
                      {check.label}
                      <span className="sr-only">: {check.hint}</span>
                    </span>
                    <ArrowRight size={13} aria-hidden="true" className="shrink-0 text-mist-2 group-hover:text-buoy" />
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </Card>
  );
}
